import { useState } from "react";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock,
  MessageSquare,
  Stethoscope,
  User,
} from "lucide-react";
import { formatDateTime, formatDuration } from "@repo/utils";
import {
  DOMAINS_CONFIG,
  DIMENSIONS_ORDER,
  ECHELLE_DESCRIPTIONS,
} from "./copsoq3.constants";
import { classeDomaine } from "./copsoq3.utils";
import {
  Copsoq3ReponsesNonScorees,
  type ReponseNonScoreeCopsoq3,
} from "./Copsoq3ReponsesNonScorees";

interface Copsoq3Reponse {
  name: string;
  question: string;
  echelle: string;
  domaine: string;
  reponse: string;
  score: number;
}

interface Copsoq3Donnees {
  type: "form";
  template: "copsoq3";
  reponses: Record<string, Copsoq3Reponse>;
  /** Partie 1. Absent = passation antérieure à la partie 1. */
  profil?: ReponseNonScoreeCopsoq3[];
  /** Partie 3. Absent = passation antérieure à la partie 3. */
  sante?: ReponseNonScoreeCopsoq3[];
}

interface CandidatInfo {
  prenom: string;
  nom: string;
  email: string;
  dateDebut?: number;
  dateFin?: number;
  tempsPasse?: number;
}

interface Copsoq3ResultsProps {
  donnees: Copsoq3Donnees;
  candidatInfo: CandidatInfo;
  /** Libellé de la version du questionnaire passée ; non affichée si absent. */
  version?: string;
}


function getMaxScore(echelle: string, questionCount: number): number {
  if (echelle === "Satisfaction au travail") return 3;
  if (echelle === "Santé auto évaluée") return 4;
  return questionCount * 4;
}

function getMultiplier(echelle: string, questionCount: number): number {
  if (echelle === "Satisfaction au travail") return 33.33;
  if (echelle === "Santé auto évaluée") return 25;
  if (questionCount === 3) return 8.34;
  return 12.5;
}

// Zones de SÉVÉRITÉ, pas des catégories : elles s'ordonnent, donc `--etat-*`.
const ZONE_COLORS = {
  red: "var(--etat-erreur)",
  orange: "var(--etat-alerte)",
  green: "var(--etat-fait)",
} as const;

function getScoreZone(
  echelle: string,
  scoreBrut: number,
  questionCount: number,
): keyof typeof ZONE_COLORS {
  if (echelle === "Satisfaction au travail") {
    if (scoreBrut <= 1) return "red";
    if (scoreBrut <= 2) return "orange";
    return "green";
  }
  if (echelle === "Santé auto évaluée") {
    if (scoreBrut <= 1) return "red";
    if (scoreBrut <= 3) return "orange";
    return "green";
  }
  const max = questionCount * 4;
  if (max === 8) {
    if (scoreBrut <= 2) return "red";
    if (scoreBrut <= 5) return "orange";
    return "green";
  }
  // max === 12
  if (scoreBrut <= 3) return "red";
  if (scoreBrut <= 7) return "orange";
  return "green";
}

function GaussianBars({ classe, color }: { classe: number; color: string }) {
  const heights = [12, 34, 48, 34, 12];
  const barWidth = 22;
  const gap = 5;
  const maxHeight = 48;
  const svgWidth = heights.length * barWidth + (heights.length - 1) * gap;

  return (
    <div className="flex flex-col items-center">
      <svg width={svgWidth} height={maxHeight}>
        {heights.map((h, i) => (
          <rect
            key={i}
            x={i * (barWidth + gap)}
            y={maxHeight - h}
            width={barWidth}
            height={h}
            fill={color}
            opacity={i + 1 === classe ? 1 : 0.18}
            rx={4}
          />
        ))}
      </svg>
      <div className="flex mt-1" style={{ width: svgWidth }}>
        {[1, 2, 3, 4, 5].map((n, i) => (
          <div
            key={n}
            style={{
              width: i < 4 ? barWidth + gap : barWidth,
              textAlign: "center",
              fontSize: 10,
              fontWeight: n === classe ? 700 : 400,
              color: n === classe ? color : "var(--ardoise)",
            }}
          >
            {n}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReponsesDropdown({
  reponses,
}: {
  reponses: Record<string, Copsoq3Reponse>;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const sortedReponses = Object.values(reponses).sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  if (sortedReponses.length === 0) return null;

  return (
    <Card className="w-full">
      <CardHeader
        className="cursor-pointer hover:bg-muted/50 transition-colors select-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isOpen ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )}
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">Réponses du candidat</CardTitle>
          </div>
          <Badge variant="outline">{sortedReponses.length} réponses</Badge>
        </div>
      </CardHeader>
      {isOpen && (
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-4 py-2 text-left font-medium text-muted-foreground w-12">
                  #
                </th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                  Question
                </th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground w-1/3">
                  Réponse
                </th>
                <th className="px-4 py-2 text-right font-medium text-muted-foreground w-16">
                  Score
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedReponses.map((rep) => (
                <tr
                  key={rep.name}
                  className="border-t border-border hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground align-top">
                    {rep.name}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <p className="text-xs text-muted-foreground mb-0.5">
                      {rep.echelle} — {rep.domaine}
                    </p>
                    <p>{rep.question}</p>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground align-top">
                    {rep.reponse}
                  </td>
                  <td className="px-4 py-3 text-right font-mono align-top">
                    {rep.score}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      )}
    </Card>
  );
}

export function Copsoq3Results({
  donnees,
  candidatInfo,
  version,
}: Copsoq3ResultsProps) {
  if (donnees.type !== "form" || donnees.template !== "copsoq3") {
    return <div>Type de données incorrect</div>;
  }

  const domaineMap: Record<
    string,
    Record<string, { score: number; count: number }>
  > = {};

  for (const rep of Object.values(donnees.reponses)) {
    const domaine = rep.domaine;
    const echelle = rep.echelle;
    if (!domaineMap[domaine]) domaineMap[domaine] = {};
    const echelles = domaineMap[domaine];
    if (!echelles[echelle]) echelles[echelle] = { score: 0, count: 0 };
    echelles[echelle].score += rep.score;
    echelles[echelle].count++;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 p-4 md:p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">COPSOQ 3</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Évaluation des risques psychosociaux
            </p>
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {candidatInfo.prenom} {candidatInfo.nom} — {candidatInfo.email}
            </div>
          </div>
          <Badge variant="success">Terminée</Badge>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {version && (
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Version</span>
                <p className="font-medium">{version}</p>
              </div>
            </div>
          )}
          {candidatInfo.tempsPasse !== undefined && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Temps passé</span>
                <p className="font-medium">
                  {formatDuration(candidatInfo.tempsPasse)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateDebut && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de début</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateDebut)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateFin && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de fin</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateFin)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Présentation */}
      <div className="mb-6 px-4 md:px-6 space-y-3 text-sm text-muted-foreground max-w-full wrap-break-word">
        <h2 className="text-base font-semibold text-foreground">Présentation du COPSOQ</h2>
        <p>
          Le <span className="font-semibold text-foreground">COPSOQ</span>{" "}
          (Copenhagen Psychosocial Questionnaire) est un outil de référence en
          matière d'évaluation des facteurs de risques psychosociaux au travail.
          Elaboré par une équipe de chercheurs du Danish National Institute of
          Occupational Health de Copenhague au début des années 2000, le COPSOQ
          a ensuite été développé et validé dans de nombreuses langues au sein
          d'un réseau international.
        </p>
        <p>
          La version courte française du{" "}
          <span className="font-semibold text-foreground">COPSOQ III</span>{" "}
          comporte 54 items mesurant 24 facteurs de risque répartis dans 6
          domaines.
        </p>
      </div>

      {donnees.profil && (
        <div className="mb-6">
          <Copsoq3ReponsesNonScorees
            titre="Profil du répondant"
            icone={User}
            reponses={donnees.profil}
          />
        </div>
      )}

      {/* Domaines */}
      <div className="space-y-6">
        {DOMAINS_CONFIG.map((domainConfig) => {
          const echelles = domaineMap[domainConfig.name];
          if (!echelles) return null;

          const orderedDimensions =
            DIMENSIONS_ORDER[domainConfig.name] ?? Object.keys(echelles);

          const totalBrut = orderedDimensions.reduce((sum, echelle) => {
            const data = echelles[echelle];
            return sum + (data?.score ?? 0);
          }, 0);

          return (
            <Card
              key={domainConfig.name}
              className={`overflow-hidden border ${domainConfig.border}`}
            >
              <CardHeader className={`${domainConfig.bg} py-3 px-0`}>
                <div className="flex items-end w-full">
                  <div className="pl-4 pr-4 w-1/2">
                    <CardTitle className={`text-base ${domainConfig.text}`}>
                      {domainConfig.name}
                    </CardTitle>
                  </div>
                  <div className="flex items-end gap-3 px-4 w-1/2">
                    <span className={`text-sm ${domainConfig.text}`}>
                      Score étalonné :{" "}
                      <span className="font-semibold font-mono">
                        {classeDomaine(domainConfig.name, totalBrut)}/5
                      </span>
                    </span>
                    <GaussianBars
                      classe={classeDomaine(domainConfig.name, totalBrut)}
                      color={domainConfig.color}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                        Dimension
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground w-1/2">
                        Score
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderedDimensions.map((echelle) => {
                      const data = echelles[echelle];
                      if (!data) return null;

                      const maxScore = getMaxScore(echelle, data.count);
                      const multiplier = getMultiplier(echelle, data.count);
                      const scoreBrut = data.score;
                      const scoreNorm = Math.round(scoreBrut * multiplier);
                      const pct = (scoreBrut / maxScore) * 100;
                      const zone = getScoreZone(echelle, scoreBrut, data.count);
                      const description = ECHELLE_DESCRIPTIONS[echelle];

                      return (
                        <tr
                          key={echelle}
                          className="border-t border-border hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-4 py-2">
                            <p className="line-clamp-2 text-sm">
                              <span className="font-bold">{echelle}</span>
                              {description && (
                                <span className="text-muted-foreground ml-1.5">
                                  — {description}
                                </span>
                              )}
                            </p>
                          </td>
                          <td className="px-4 py-2 w-1/2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-muted rounded-full h-2.5 overflow-hidden">
                                <div
                                  className="h-full rounded-full"
                                  style={{
                                    width: `${pct}%`,
                                    backgroundColor: ZONE_COLORS[zone],
                                  }}
                                />
                              </div>
                              <span
                                className="text-xs font-mono font-semibold w-9 shrink-0"
                                style={{ color: ZONE_COLORS[zone] }}
                              >
                                {scoreNorm}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {donnees.sante && (
        <div className="mt-6">
          <Copsoq3ReponsesNonScorees
            titre="Questions médicales"
            icone={Stethoscope}
            reponses={donnees.sante}
          />
        </div>
      )}

      {/* Réponses */}
      <div className="mt-6">
        <ReponsesDropdown reponses={donnees.reponses} />
      </div>
    </div>
  );
}
